const sectionTitleStyle = {
  fontFamily: "var(--font-serif)",
  fontStyle: "italic",
  fontSize: "20px",
  color: "var(--color-ink)",
  margin: 0,
};

const bodyStyle = { fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 };
const listStyle = { display: "flex", flexDirection: "column", gap: "10px", margin: 0, paddingLeft: "20px" };
const taglineStyle = {
  fontFamily: "var(--font-serif)",
  fontSize: "24px",
  lineHeight: 1.3,
  color: "var(--color-ink)",
  margin: 0,
};

function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <p style={sectionTitleStyle}>{title}</p>
      {children}
    </div>
  );
}

/**
 * Sponsoring pitch copy, as provided directly by the association. Two
 * factual corrections made against our own already-published facts:
 * - "keine Anteilseigner" (no shareholders) is not accurate — the
 *   Gesellschaftsvertrag page lists BrainEntertainments Holding UG as the
 *   one Gesellschafter. Kept the true, intended point instead (§9 of the
 *   Gesellschaftsvertrag: Gesellschafter receive no profit share).
 * - "[deine Telefonnummer]" placeholder replaced with the real number
 *   already published on Impressum/Datenschutz.
 * - "Tales & Legends" (with spaces) normalized to "Tales&Legends" per the
 *   site-wide brand-name convention.
 * German is the primary/authoritative wording (source text was German);
 * English is a courtesy translation.
 */
export function SponsoringDe() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
      <p style={taglineStyle}>Werde Teil der Legende</p>

      <p style={bodyStyle}>
        Stell dir vor: Hunderte von Menschen tauchen für ein Wochenende in eine andere Welt ein. Ritter in
        funkelnder Rüstung, Händler auf belebten Marktplätzen, Magier, die Schicksale weben – und mittendrin: deine
        Marke, als Förderer einer Erfahrung, die Menschen noch Jahre später beschäftigt.
      </p>
      <p style={bodyStyle}>
        Tales&amp;Legends erschafft Fantasy-Liverollenspiel-Events, bei denen Geschichten nicht gelesen, sondern
        gelebt werden. Unsere Teilnehmenden sind keine passiven Zuschauer – sie sind Protagonist:innen. Und du
        kannst Teil dieser Geschichte werden.
      </p>

      <Section title="Warum Tales&Legends?">
        <p style={bodyStyle}>
          Tales&amp;Legends ist als gemeinnützige Unternehmergesellschaft (gUG) organisiert – denn für uns ist LARP
          mehr als ein Geschäftsmodell: Es ist Kulturarbeit. Wir glauben daran, dass kreatives Rollenspiel,
          gemeinschaftliches Erzählen und das Leben in anderen Welten einen echten gesellschaftlichen Wert haben –
          für Fantasie, Empathie und Gemeinschaft.
        </p>
        <p style={bodyStyle}>
          Das bedeutet für dich als Sponsor: Deine Unterstützung fließt direkt in die Qualität unserer Events und in
          den Aufbau einer lebendigen Community. Keine Gewinnausschüttung an Gesellschafter — ein Team, das sein
          Herzblut in jede Veranstaltung steckt.
        </p>
        <p style={bodyStyle}>
          Da Sponsoring eine unternehmerische Gegenleistung darstellt, kannst du deine Aufwendungen in der Regel als
          Betriebsausgabe geltend machen. Im Gegenzug erhältst du von uns eine transparente Dokumentation der
          vereinbarten Leistungen.
        </p>
      </Section>

      <Section title="Was uns darüber hinaus auszeichnet">
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Eigene Fantasy-Welt</strong> – unsere Events spielen in einem durchdachten, eigenen Setting mit
            Geschichte, Fraktionen und lebendiger Lore.
          </li>
          <li style={bodyStyle}>
            <strong>Aktive Community</strong> – unsere Teilnehmenden kommen immer wieder, empfehlen weiter und leben
            LARP das ganze Jahr.
          </li>
          <li style={bodyStyle}>
            <strong>Echte Wirkung</strong> – du förderst nicht nur ein Event, sondern eine kulturelle Bewegung.
          </li>
        </ul>
      </Section>

      <Section title="Was wir dir bieten">
        <p style={bodyStyle}>
          Je nach Partnerschaft stehen dir verschiedene Wege offen, präsent zu sein – von der Nennung als
          offizieller Förderer über einen eigenen Stand auf dem Event-Marktplatz bis hin zu weiteren individuellen
          Möglichkeiten. Ob dein Logo auf dem Programm erscheint, dein Produkt bei uns verkauft wird oder dein Name
          in der SL-Ansprache klingt: Wir gestalten die Zusammenarbeit so, dass sie authentisch zur Welt von
          Tales&amp;Legends passt – und zu deinen Zielen.
        </p>
      </Section>

      <Section title="Lass uns gemeinsam Geschichte schreiben">
        <p style={bodyStyle}>
          Du bist neugierig geworden? Dann freuen wir uns auf deine Nachricht. Wir erzählen dir gern mehr über
          unsere Events, unsere Community und die Möglichkeiten, die eine Partnerschaft mit Tales&amp;Legends dir
          bietet.
        </p>
        <p style={bodyStyle}>
          Schreib uns unter: <a href="mailto:hello@talesandlegends.org">hello@talesandlegends.org</a>
          <br />
          Oder ruf einfach an: <a href="tel:+4915222584587">01522 2584587</a>
        </p>
        <p style={{ ...bodyStyle, fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
          Die Legende wartet. Werde Teil davon.
        </p>
      </Section>
    </div>
  );
}

/** Courtesy translation — German is the primary wording (see SponsoringDe). */
export function SponsoringEn() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
      <p style={taglineStyle}>Become Part of the Legend</p>

      <p style={bodyStyle}>
        Picture it: hundreds of people stepping into another world for a whole weekend. Knights in gleaming armor,
        merchants at bustling markets, mages weaving fate — and right in the middle of it: your brand, supporting an
        experience that stays with people for years afterward.
      </p>
      <p style={bodyStyle}>
        Tales&amp;Legends creates fantasy live-action-roleplay events where stories aren't read, but lived. Our
        participants aren't passive spectators — they're protagonists. And you can become part of that story.
      </p>

      <Section title="Why Tales&Legends?">
        <p style={bodyStyle}>
          Tales&amp;Legends is organized as a non-profit UG (gUG) — because for us, LARP is more than a business
          model: it's cultural work. We believe that creative roleplay, collective storytelling, and living in other
          worlds have real social value — for imagination, empathy, and community.
        </p>
        <p style={bodyStyle}>
          For you as a sponsor, that means: your support flows directly into the quality of our events and into
          building a living community. No profit distributed to shareholders — just a team that pours its heart
          into every event.
        </p>
        <p style={bodyStyle}>
          Since sponsoring is a business transaction with something given in return, you can generally claim your
          contribution as a business expense. In return, we provide transparent documentation of the agreed
          services.
        </p>
      </Section>

      <Section title="What else sets us apart">
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Our own fantasy world</strong> — our events take place in a carefully built, original setting
            with history, factions, and living lore.
          </li>
          <li style={bodyStyle}>
            <strong>An active community</strong> — our participants keep coming back, recommend us to others, and
            live LARP all year round.
          </li>
          <li style={bodyStyle}>
            <strong>Real impact</strong> — you're not just supporting one event, but a cultural movement.
          </li>
        </ul>
      </Section>

      <Section title="What we offer you">
        <p style={bodyStyle}>
          Depending on the partnership, there are various ways to be present — from being named as an official
          sponsor, to having your own stand at the event marketplace, to further individual options. Whether your
          logo appears in the program, your product is sold at our events, or your name is mentioned in the game
          masters' announcements: we shape the collaboration so it fits authentically into the world of
          Tales&amp;Legends — and your goals.
        </p>
      </Section>

      <Section title="Let's write history together">
        <p style={bodyStyle}>
          Curious? We'd love to hear from you. We're happy to tell you more about our events, our community, and
          the possibilities a partnership with Tales&amp;Legends offers.
        </p>
        <p style={bodyStyle}>
          Write to us at: <a href="mailto:hello@talesandlegends.org">hello@talesandlegends.org</a>
          <br />
          Or just give us a call: <a href="tel:+4915222584587">01522 2584587</a>
        </p>
        <p style={{ ...bodyStyle, fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
          The legend is waiting. Become part of it.
        </p>
      </Section>
    </div>
  );
}
